import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { IntegrationCoaMapping } from "@common/schemas/domains/accounting/integrationCoaMapping.schema";
import { Account } from "@common/types/domains/accounting/account";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import type {
    IntegrationCoaMappingTableCallbacks,
    IntegrationCoaMappingTableOptions,
} from "../integrationCoaMappingTableMeta";

export const MappedAccountCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        IntegrationCoaMapping,
        string | null
    >) => {
        const mapping = row.original;
        const { callbacks, options } = useViewTableMeta<
            IntegrationCoaMappingTableCallbacks,
            IntegrationCoaMappingTableOptions
        >(table);

        const selectedAccount =
            mapping.accountId !== null
                ? options?.accountsById[mapping.accountId]
                : undefined;

        const handleChange = useCallback(
            (account: Account | null) => {
                callbacks?.onSelectAccount({
                    mappingId: mapping.id,
                    accountId: account?.id ?? null,
                });
            },
            [callbacks, mapping.id],
        );

        if (!options) {
            return null;
        }

        return (
            <ViewTableCell>
                <AccountCombobox
                    entityId={options.entityId}
                    selectedAccount={selectedAccount}
                    onChange={handleChange}
                    readOnly={!options.canEditMappings}
                    canClear
                    showAccountCode
                />
            </ViewTableCell>
        );
    },
);
MappedAccountCell.displayName = "MappedAccountCell";
